/* eslint-disable @typescript-eslint/indent */
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { themes } from 'kb-shared';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Text = styled.div`
  text-align: left;
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  padding-top: 14px;
`;

const IndicatorDotsOverlayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  flex-direction: row;
  height: 100%;
  z-index: 1;
`;

type Props = {
  steps: string[];
  selectedStep: number;
};

export default class MobileStepTracker extends React.Component<Props> {
  static defaultProps: Props = {
    steps: [],
    selectedStep: 0
  };

  renderSteps(): ReactNode {
    return (
      <IndicatorDotsOverlayContainer>
        {this.props.steps.map((step: string, index: number) => {
          return <IndicatorIcon active={this.props.selectedStep >= index} key={step} />;
        })}
      </IndicatorDotsOverlayContainer>
    );
  }

  render() {
    const numSteps = this.props.steps.length;
    const { selectedStep } = this.props;
    // Clamp the selectedStep to a max value in order to avoid "overflow" of the line
    const selectedStepClamp = Math.min(selectedStep, numSteps - 1);
    return (
      <Container>
        <IndicatorLine progress={selectedStepClamp / (numSteps - 1)}>
          {this.renderSteps()}
        </IndicatorLine>
        {//Do not render text if step is greater than # of steps.
        selectedStep >= numSteps ? null : (
          <Text>
            Step {selectedStep + 1}: {this.props.steps[selectedStep]}
          </Text>
        )}
      </Container>
    );
  }
}

// IndicatorLine
const STEPPER_WIDTH = 200;

interface ActiveProps {
  active?: boolean;
}

const IndicatorLineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: ${`${STEPPER_WIDTH}px`};
  height: 6px;
  background-color: ${themes.colors.KBBeige};
  ${({ active }: ActiveProps) => `
    background-color: ${active ? themes.colors.KBYellow : themes.colors.KBBeige};
    transition: all 400ms ease 0s;
  `}
`;

interface IndicatorFillOverlayProps {
  progress: number;
}

const IndicatorFillOverlay = styled.div`
  position: absolute;
  height: 6px;
  background-color: ${themes.colors.KBYellow};
  width: ${(props: IndicatorFillOverlayProps) => `${props.progress * STEPPER_WIDTH}px`};
  transition: all 600ms ease 0s;
`;

type IndicatorLineProps = {
  children?: ReactNode | null;
  progress: number; // number from 0.0-1.0
};

const IndicatorLine = (props: IndicatorLineProps) => {
  return (
    <IndicatorLineContainer>
      <IndicatorFillOverlay progress={props.progress} />
      {props.children}
    </IndicatorLineContainer>
  );
};

// IndicatorIcon

const Indicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  ${({ active }: ActiveProps) => `
    background-color: ${active ? themes.colors.KBYellow : themes.colors.KBBeige};
    transition: all 800ms ease 200ms;
  `}
`;

const InnerIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  ${({ active }: ActiveProps) => `
    background-color: ${active ? themes.colors.KBNight : 'white'};
    transition: all 800ms ease 200ms;
  `}
`;

const IndicatorContainer = styled.div`
  display: flex;
`;

type IndicatorIconProps = {
  active: boolean;
};

const IndicatorIcon = (props: IndicatorIconProps) => {
  return (
    <IndicatorContainer>
      <Indicator active={props.active}>
        <InnerIndicator active={props.active} />
      </Indicator>
    </IndicatorContainer>
  );
};
